import { activeWorkspace } from "@/workspace/file-ops";
import { openAt } from "@/workspace/search-panel";
import { aiAllowed } from "@/lib/ai-access";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, History, LogIn, LogOut, Plus, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { create } from "zustand";
import type { ClaudeCodeSession, ClaudeCodeTokens, ClaudePlanWindow } from "@ide/shared/desktop";
import { ClaudeMark } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Tip,
} from "@/components/ui/overlay";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import { useDesktopUi } from "@/desktop/ui";
import { useNow } from "@/lib/use-now";
import { cn, timeAgo } from "@/lib/utils";
import { claudeEngineInstalled, embedClaude } from "@/workspace/code-engine";
import { useOptionalWs, useWs, type WorkspaceContextValue } from "@/workspace/context";
import {
  planOf,
  refreshClaudeCode,
  signInWithClaudeCode,
  signOutOfClaudeCode,
  useClaudeCode,
} from "./claude-login";
import { AiWorkingSet } from "@/assistant/ai-working-set";

/**
 * Claude Code no Kompil, como no VS Code: o painel da direita mostra o plugin oficial da Anthropic
 * (a mesma conversa, com a caixa de prompt, diffs e permissões dele) e a barra da esquerda mostra a
 * conta, os limites do plano com o horário de renovação e as conversas do projeto. O motor VS Code
 * só hospeda o plugin: nunca aparece como janela. Uso e limites vêm do próprio Claude Code (o Kompil
 * não lê a credencial) e nada é digitado na conversa por conta própria — só os seus prompts.
 */

const api = () => desktop?.claudeCode ?? null;

/** Status da conta (lê uma vez ao montar). */
export function useClaudeCodeAuth() {
  const auth = useClaudeCode((s) => s.auth);
  const phase = useClaudeCode((s) => s.phase);
  useEffect(() => {
    if (api() && !useClaudeCode.getState().auth) void refreshClaudeCode();
  }, []);
  return { auth, phase, available: Boolean(api()) };
}

/** Limites do plano (5 h e 7 dias) com o horário de renovação. */
export function useClaudePlanUsage(enabled = true) {
  return useQuery({
    queryKey: ["claude-code", "plan"],
    queryFn: async () => (await api()?.planUsage?.()) ?? null,
    enabled: enabled && Boolean(api()?.planUsage),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/** Tokens nesta máquina (somados das conversas que o Claude Code guarda). */
export function useClaudeCodeUsage(enabled = true) {
  return useQuery({
    queryKey: ["claude-code", "usage"],
    queryFn: async () => (await api()?.usage?.()) ?? null,
    enabled: enabled && Boolean(api()?.usage),
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
}

export function useClaudeCodeSessions(projectId: string, enabled = true) {
  return useQuery({
    queryKey: ["claude-code", "sessions", projectId],
    queryFn: async () => (await api()?.sessions?.(projectId)) ?? [],
    enabled: enabled && Boolean(api()?.sessions),
    refetchInterval: 20_000,
  });
}

// ───────────── Painel da direita: o plugin oficial embutido ─────────────

/** Estado do Claude embutido no projeto aberto. */
export const useClaudeEmbed = create<{
  projectId: string | null;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  /** Conversa retomada pela barra da esquerda (para marcar na lista). */
  session: string | null;
}>(() => ({ projectId: null, status: "idle", error: null, session: null }));

/** Aberturas em andamento por projeto (montar o painel e o pré-carregamento não abrem duas vezes). */
const opening = new Map<string, Promise<void>>();

/**
 * Abre o Claude no painel: a última conversa, uma nova ou uma anterior. `show: false` só pré-carrega
 * (o projeto abriu e o Claude fica pronto em segundo plano, sem mudar o painel da esquerda).
 */
export function openClaude(
  ws: WorkspaceContextValue,
  request: { session?: string; new?: boolean } = {},
  options: { show?: boolean } = {},
): Promise<void> {
  const projectId = ws.detail.project.id.toLowerCase();
  if (options.show !== false) useDesktopUi.getState().set({ side: "claude" });
  const plain = !request.session && !request.new;
  const pending = opening.get(projectId);
  if (plain && pending) return pending;
  const run = doOpen(ws, projectId, request).finally(() => {
    if (opening.get(projectId) === run) opening.delete(projectId);
  });
  opening.set(projectId, run);
  return run;
}

async function doOpen(
  ws: WorkspaceContextValue,
  projectId: string,
  request: { session?: string; new?: boolean },
) {
  listenReady();
  const state = useClaudeEmbed.getState();
  const first = state.projectId !== projectId || state.status !== "ready";
  if (first) useClaudeEmbed.setState({ projectId, status: "loading", error: null });
  if (request.session) useClaudeEmbed.setState({ session: request.session });
  else if (request.new) useClaudeEmbed.setState({ session: null });
  const ready = () => {
    const s = useClaudeEmbed.getState();
    if (s.projectId === projectId && s.status === "loading") useClaudeEmbed.setState({ status: "ready" });
  };
  try {
    const res = await embedClaude(ws, request);
    if (!res.ok) {
      useClaudeEmbed.setState({
        projectId,
        status: "error",
        error: res.error ?? "Não deu para abrir o Claude Code.",
      });
      return;
    }
    if (res.ready) return ready();
  } catch (err) {
    useClaudeEmbed.setState({
      projectId,
      status: "error",
      error: err instanceof Error ? err.message : "Não deu para abrir o Claude Code.",
    });
    return;
  }
  // O app avisa quando o plugin abriu (confere a tela a cada 300 ms). Por garantia, mostra depois de
  // 30 s mesmo sem aviso (versões antigas do app não avisam).
  if (first) setTimeout(ready, desktop?.codeEngine?.onEmbedReady ? 30_000 : 3_000);
}

/** Projeto aberto, Claude Code conectado e plugin já instalado: carrega o Claude em segundo plano. */
export function usePrewarmClaude(ws: WorkspaceContextValue) {
  const projectId = ws.detail.project.id.toLowerCase();
  const loggedIn = Boolean(useClaudeCodeAuth().auth?.loggedIn);
  const wsRef = useRef(ws);
  useEffect(() => {
    wsRef.current = ws;
  });
  const allowed = aiAllowed(ws.detail);
  useEffect(() => {
    if (!allowed || !loggedIn || !desktop?.codeEngine?.embedState) return;
    let cancelled = false;
    // Deixa o projeto terminar de abrir antes.
    const timer = setTimeout(() => {
      void claudeEngineInstalled().then((ok) => {
        const s = useClaudeEmbed.getState();
        if (!ok || cancelled || (s.projectId === projectId && s.status !== "idle" && s.status !== "error"))
          return;
        void openClaude(wsRef.current, {}, { show: false });
      });
    }, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [projectId, loggedIn, allowed]);
}

/**
 * Menus, diálogos e popovers do Kompil que cobrem o painel: a tela do Claude (que fica por cima da
 * página) se esconde enquanto estão abertos. Dicas (tooltips) e o que não encosta no painel não contam.
 */
function overlayOver(slot: DOMRect): boolean {
  const layers = document.querySelectorAll<HTMLElement>(
    '[role="dialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]',
  );
  for (const el of layers) {
    if (el.querySelector('[role="tooltip"]') || el.getAttribute("role") === "tooltip") continue;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) continue;
    if (r.right > slot.left && r.left < slot.right && r.bottom > slot.top && r.top < slot.bottom) return true;
  }
  return false;
}

/** Na primeira abertura, a tela só aparece quando o plugin do Claude já abriu (sem o VS Code piscar). */
let readyListener = false;
function listenReady() {
  const engine = desktop?.codeEngine;
  if (readyListener || !engine?.onEmbedReady) return;
  readyListener = true;
  // Link de arquivo na conversa: abre no editor do Kompil (à esquerda), na linha indicada.
  engine.onOpenFile?.((e) => {
    const ws = activeWorkspace.current;
    if (!ws || ws.detail.project.id.toLowerCase() !== e.projectId) return;
    const id = [...ws.session.tree().paths].find(([, p]) => p === e.path)?.[0];
    if (id) openAt(id, e.line);
  });
  engine.onEmbedReady((projectId) => {
    if (useClaudeEmbed.getState().projectId === projectId) useClaudeEmbed.setState({ status: "ready" });
  });
}

function AccountLine() {
  const auth = useClaudeCode((s) => s.auth);
  if (!auth?.loggedIn) return null;
  const plan = planOf(auth.subscription);
  return (
    <span className="min-w-0 truncate text-[11.5px] text-fg-muted" title={auth.email}>
      {auth.email}
      {auth.method === "claude.ai" ? ` · ${plan ? `Claude ${plan}` : "assinatura"}` : " · Console"}
    </span>
  );
}

function ConversationsMenu({ ws }: { ws: WorkspaceContextValue }) {
  const sessions = useClaudeCodeSessions(ws.detail.project.id);
  // Conversas de outras pastas (VS Code, terminal): dá para continuar aqui.
  const all = useQuery({
    queryKey: ["claude-code", "all-sessions", ws.detail.project.id],
    queryFn: () => desktop!.claudeCode!.allSessions!(ws.detail.project.id),
    enabled: typeof desktop?.claudeCode?.allSessions === "function",
    staleTime: 60_000,
  });
  const others = (all.data ?? []).filter((s) => !s.here);
  return (
    <Menu>
      <Tip label="Conversas anteriores">
        <MenuTrigger asChild>
          <button
            type="button"
            aria-label="Conversas anteriores"
            className="flex h-[22px] w-[22px] items-center justify-center rounded-[4px] text-fg-muted hover:bg-bg-emphasis hover:text-fg"
          >
            <History className="h-3.5 w-3.5" />
          </button>
        </MenuTrigger>
      </Tip>
      <MenuContent align="end" className="w-80">
        <MenuLabel>Retomar uma conversa</MenuLabel>
        {(sessions.data ?? []).slice(0, 15).map((s) => (
          <MenuItem key={s.id} onSelect={() => void openClaude(ws, { session: s.id })}>
            <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
              <span className="truncate">{s.title}</span>
              <span className="shrink-0 text-[11px] text-fg-subtle">{timeAgo(s.updatedAt)}</span>
            </span>
          </MenuItem>
        ))}
        {sessions.data && !sessions.data.length ? (
          <p className="px-2 py-1.5 text-[12px] text-fg-subtle">Nenhuma conversa neste projeto ainda.</p>
        ) : null}
        {others.length ? (
          <>
            <MenuSeparator />
            <MenuLabel>Outras conversas neste computador</MenuLabel>
            {others.slice(0, 15).map((s) => (
              <MenuItem
                key={s.id}
                onSelect={() =>
                  void (async () => {
                    // A conversa vem para a pasta do projeto e o Claude retoma de onde parou.
                    const ok = await desktop?.claudeCode?.importSession?.(ws.detail.project.id, s.id);
                    if (!ok) {
                      toast.error("Não deu para trazer essa conversa.");
                      return;
                    }
                    await openClaude(ws, { session: s.id });
                  })()
                }
              >
                <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block truncate">{s.title}</span>
                    {s.folder ? (
                      <span className="block truncate text-[10.5px] text-fg-subtle">{s.folder}</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-[11px] text-fg-subtle">{timeAgo(s.updatedAt)}</span>
                </span>
              </MenuItem>
            ))}
          </>
        ) : null}
      </MenuContent>
    </Menu>
  );
}

/**
 * O plugin Claude Code oficial no painel da direita. A tela dele é uma vista presa a este espaço:
 * acompanha o tamanho e a posição do painel e some quando o painel fecha ou um menu do Kompil abre.
 */
export function ClaudeCodeSession() {
  const ws = useWs();
  const projectId = ws.detail.project.id.toLowerCase();
  const slot = useRef<HTMLDivElement>(null);
  const { status, error } = useClaudeEmbed();
  const mine = useClaudeEmbed((s) => s.projectId === projectId);

  useEffect(() => {
    const s = useClaudeEmbed.getState();
    // Também destrava um "abrindo" que ficou para trás (página recarregada, abertura que falhou).
    if (
      s.projectId !== projectId ||
      s.status === "idle" ||
      (s.status === "loading" && !opening.has(projectId))
    )
      void openClaude(ws);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const engine = desktop?.codeEngine;
    if (!engine?.embedBounds || !mine || status !== "ready") return;
    const setBounds = engine.embedBounds;
    let last = "";
    const send = () => {
      const el = slot.current;
      const r = el?.getBoundingClientRect();
      const rect =
        el && r && r.width > 0 && r.height > 0 && !overlayOver(r) && document.visibilityState === "visible"
          ? { x: r.left, y: r.top, width: r.width, height: r.height }
          : null;
      const key = JSON.stringify(rect);
      if (key === last) return;
      last = key;
      setBounds(projectId, rect);
    };
    send();
    // A posição muda sem mudar o tamanho (painéis ao lado redimensionados): confere sempre.
    const timer = setInterval(send, 150);
    window.addEventListener("resize", send);
    return () => {
      clearInterval(timer);
      window.removeEventListener("resize", send);
      setBounds(projectId, null);
    };
  }, [projectId, mine, status]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-[34px] shrink-0 items-center gap-2 border-b border-border px-3">
        <ClaudeMark className="h-3.5 w-3.5 shrink-0" />
        <span className="shrink-0 text-[12.5px] font-medium text-fg">Claude Code</span>
        <AccountLine />
        <span className="flex-1" />
        <Tip label="Nova conversa">
          <button
            type="button"
            onClick={() => void openClaude(ws, { new: true })}
            aria-label="Nova conversa"
            className="flex h-[22px] w-[22px] items-center justify-center rounded-[4px] text-fg-muted hover:bg-bg-emphasis hover:text-fg"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </Tip>
        <ConversationsMenu ws={ws} />
      </div>
      <AiWorkingSet source="claude" />
      <div ref={slot} className="relative min-h-0 flex-1 bg-[#0f0f0f]">
        {!mine || status === "loading" || status === "idle" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[12.5px] text-fg-muted">
            <Spinner className="h-4 w-4" />
            Abrindo o Claude Code…
            <span className="max-w-[260px] text-center text-[11.5px] text-fg-subtle">
              Na primeira vez o Kompil prepara o plugin oficial da Anthropic (pode levar um minuto).
            </span>
          </div>
        ) : status === "error" ? (
          <div className="absolute inset-x-4 top-6 rounded-md border border-border bg-bg-subtle p-3 text-[12.5px] text-fg-muted">
            <p className="font-medium text-fg">Não deu para abrir o Claude Code</p>
            {error ? <p className="mt-0.5">{error}</p> : null}
            <Button size="sm" variant="primary" className="mt-2" onClick={() => void openClaude(ws)}>
              Tentar de novo
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ───────────── Barra da esquerda: conta, uso e conversas ─────────────

const fmt = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mi`
    : n >= 1_000
      ? `${(n / 1_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil`
      : n.toLocaleString("pt-BR");
const total = (t: ClaudeCodeTokens) => t.input + t.output + t.cacheRead + t.cacheWrite;

/** "em 3 h 12 min" / "em 5 dias e 4 h". */
export function untilLabel(at: number, now: number): string {
  const ms = Math.max(0, at - now);
  const min = Math.round(ms / 60_000);
  if (min < 1) return "em instantes";
  if (min < 60) return `em ${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return m ? `em ${h} h ${m} min` : `em ${h} h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return `em ${d} ${d === 1 ? "dia" : "dias"}${rh ? ` e ${rh} h` : ""}`;
}

/** "às 23:19" (hoje), "amanhã às 08:00" ou "sáb., 01/11 às 11:59". */
export function whenLabel(at: number, now: number): string {
  const d = new Date(at);
  const time = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === new Date(now).toDateString()) return `às ${time}`;
  if (d.toDateString() === new Date(now + 86_400_000).toDateString()) return `amanhã às ${time}`;
  const day = d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
  return `${day} às ${time}`;
}

function Section({
  title,
  children,
  badge,
  defaultOpen = true,
}: {
  title: string;
  children: ReactNode;
  badge?: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section>
      <div className="flex h-[22px] items-center pr-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex h-full min-w-0 flex-1 items-center gap-0.5 pl-0.5 text-left text-[11px] font-bold uppercase tracking-[0.04em] text-fg"
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
          <span className="truncate">{title}</span>
        </button>
        {badge}
      </div>
      {open ? <div className="pb-3">{children}</div> : null}
    </section>
  );
}

function Row({ k, v, title }: { k: string; v: ReactNode; title?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-5 py-[3px] text-[12.5px]">
      <span className="shrink-0 text-fg-muted">{k}</span>
      <span className="min-w-0 truncate text-right text-fg" title={title}>
        {v}
      </span>
    </div>
  );
}

/** Uma janela do limite do plano: a porcentagem usada e quando renova (como no plugin do VS Code). */
function PlanRow({ label, w, now }: { label: string; w: ClaudePlanWindow; now: number }) {
  const pct = Math.round(w.utilization);
  return (
    <div className="px-5 py-1.5">
      <div className="flex items-baseline justify-between text-[12.5px]">
        <span className="text-fg">{label}</span>
        <span className={cn("tabular-nums", pct >= 90 ? "text-warning" : "text-fg")}>{pct}%</span>
      </div>
      <div className="mt-1 h-[4px] overflow-hidden rounded-full bg-bg-emphasis">
        <div
          className={cn("h-full rounded-full", pct >= 90 ? "bg-warning" : "bg-fg/85")}
          style={{ width: `${Math.max(pct > 0 ? 2 : 0, pct)}%` }}
        />
      </div>
      {w.resetsAt ? (
        <p className="mt-1 text-[11.5px] text-fg-muted">
          Renova {untilLabel(w.resetsAt, now)} · {whenLabel(w.resetsAt, now)}
        </p>
      ) : null}
    </div>
  );
}

function SessionRow({ s, active, onOpen }: { s: ClaudeCodeSession; active: boolean; onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          "flex w-full items-baseline gap-2 px-5 py-[5px] text-left text-[12.5px] hover:bg-bg-muted",
          active && "bg-bg-emphasis",
        )}
        title={`${s.title}\n${s.messages} mensagens · ${fmt(s.tokens)} tokens`}
      >
        <span className="min-w-0 flex-1 truncate text-fg">{s.title}</span>
        <span className="shrink-0 text-[11px] text-fg-subtle">{timeAgo(s.updatedAt)}</span>
      </button>
    </li>
  );
}

/**
 * Painel "Claude Code" da barra da esquerda (como o da extensão no VS Code): CONTA E USO, com os
 * limites do plano e quando renovam, e as CONVERSAS do projeto para retomar no painel.
 */
export function ClaudeCodeSidebar() {
  const ws = useOptionalWs();
  const projectId = ws?.detail.project.id ?? "";
  const { auth, phase, available } = useClaudeCodeAuth();
  const signedIn = Boolean(auth?.loggedIn);
  const plan = useClaudePlanUsage(signedIn);
  const tokens = useClaudeCodeUsage(signedIn);
  const sessions = useClaudeCodeSessions(projectId, signedIn && Boolean(projectId));
  const resumed = useClaudeEmbed((s) => s.session);
  const now = useNow(30_000);

  const shell = (children: ReactNode) => (
    <div className="flex h-full min-h-0 flex-col bg-bg-subtle">
      <div className="flex h-[35px] shrink-0 items-center gap-2 pl-5 pr-2">
        <h2 className="min-w-0 flex-1 truncate text-[11px] font-medium uppercase tracking-[0.06em] text-fg-muted">
          Claude Code
        </h2>
        <Tip label="Atualizar">
          <button
            type="button"
            aria-label="Atualizar"
            onClick={() => {
              void refreshClaudeCode();
              void plan.refetch();
              void tokens.refetch();
              void sessions.refetch();
            }}
            className="flex h-[22px] w-[22px] items-center justify-center rounded-[4px] text-fg-muted hover:bg-bg-emphasis hover:text-fg"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </Tip>
      </div>
      {children}
    </div>
  );

  if (!available)
    return shell(<p className="px-5 text-[12.5px] text-fg-muted">O Claude Code roda no Kompil Desktop.</p>);
  if (!auth)
    return shell(
      <div className="flex justify-center py-6">
        <Spinner />
      </div>,
    );
  if (!auth.loggedIn)
    return shell(
      <div className="px-5 text-[12.5px] leading-relaxed text-fg-muted">
        <p>Entre com a sua conta do Claude (Pro, Max, Team ou Enterprise) ou do Console da Anthropic.</p>
        <Button
          variant="primary"
          size="sm"
          className="mt-3 w-full"
          loading={phase === "waiting" || phase === "installing"}
          onClick={() => {
            useDesktopUi.getState().set({ side: "claude" });
            void signInWithClaudeCode("claudeai");
          }}
        >
          <LogIn className="h-3.5 w-3.5" /> Entrar com o Claude
        </Button>
      </div>,
    );

  const p = plan.data;
  const t = tokens.data;
  const planName = planOf(auth.subscription ?? p?.subscription ?? undefined);

  return shell(
    <div className="min-h-0 flex-1 overflow-y-auto">
      <Section title="Conta e uso">
        <Row k="E-mail" v={auth.email ?? "—"} title={auth.email} />
        <Row
          k="Plano"
          v={auth.method === "claude.ai" ? `Claude ${planName ?? ""}`.trim() : "Console (uso da API)"}
        />
        {auth.org ? <Row k="Organização" v={auth.org} title={auth.org} /> : null}

        <p className="px-5 pb-0.5 pt-3 text-[11px] font-semibold uppercase tracking-[0.04em] text-fg-subtle">
          Uso do plano
        </p>
        {plan.isLoading ? (
          <div className="flex justify-center py-3">
            <Spinner />
          </div>
        ) : p && (p.fiveHour || p.sevenDay) ? (
          <>
            {p.fiveHour ? <PlanRow label="Sessão (5 h)" w={p.fiveHour} now={now} /> : null}
            {p.sevenDay ? <PlanRow label="Semana (7 dias)" w={p.sevenDay} now={now} /> : null}
            {p.sevenDayOpus ? <PlanRow label="Opus na semana" w={p.sevenDayOpus} now={now} /> : null}
            {p.sevenDaySonnet ? <PlanRow label="Sonnet na semana" w={p.sevenDaySonnet} now={now} /> : null}
            <p className="px-5 pt-1 text-[11px] text-fg-subtle">Atualizado {timeAgo(p.at)}.</p>
          </>
        ) : (
          <p className="px-5 text-[12px] text-fg-subtle">
            {auth.method === "claude.ai"
              ? "A Anthropic não informou os limites agora. Tente atualizar em instantes."
              : "Contas do Console não têm limite de sessão: o uso é cobrado por token."}
          </p>
        )}

        {t && (total(t.today) || total(t.week)) ? (
          <>
            <p className="px-5 pb-0.5 pt-3 text-[11px] font-semibold uppercase tracking-[0.04em] text-fg-subtle">
              Tokens neste computador
            </p>
            <Row k="Hoje" v={`${fmt(total(t.today))} · ${t.today.messages} respostas`} />
            <Row k="7 dias" v={`${fmt(total(t.week))} · ${t.week.messages} respostas`} />
          </>
        ) : null}
      </Section>

      {ws ? (
        <Section
          title="Conversas"
          badge={
            <span className="rounded-full bg-bg-emphasis px-1.5 text-[10.5px] font-semibold leading-[16px] text-fg">
              {sessions.data?.length ?? 0}
            </span>
          }
        >
          <div className="px-3 pb-1">
            <button
              type="button"
              onClick={() => void openClaude(ws, { new: true })}
              className="flex w-full items-center gap-2 rounded-[3px] px-2 py-1.5 text-left text-[12.5px] font-medium text-fg hover:bg-bg-muted"
            >
              <Plus className="h-3.5 w-3.5" /> Nova conversa
            </button>
          </div>
          {sessions.isLoading ? (
            <div className="flex justify-center py-3">
              <Spinner />
            </div>
          ) : sessions.data?.length ? (
            <ul>
              {sessions.data.map((s) => (
                <SessionRow
                  key={s.id}
                  s={s}
                  active={resumed === s.id}
                  onOpen={() => void openClaude(ws, { session: s.id })}
                />
              ))}
            </ul>
          ) : (
            <p className="px-5 text-[12px] text-fg-subtle">Nenhuma conversa neste projeto ainda.</p>
          )}
        </Section>
      ) : null}

      <div className="px-5 pb-4 pt-1">
        <button
          type="button"
          onClick={() => void signOutOfClaudeCode()}
          className="inline-flex items-center gap-1.5 text-[12px] text-fg-muted hover:text-fg"
        >
          <LogOut className="h-3.5 w-3.5" /> Sair da conta do Claude
        </button>
      </div>
    </div>,
  );
}
